import "./global.css";

import { Toaster } from "@/components/ui/toaster";
import { createRoot } from "react-dom/client";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { ClerkProvider, useAuth } from "@clerk/clerk-react";
import "./lib/debug";
import WorkspaceHome from "./pages/WorkspaceHome";
import StarryBackground from "@/components/layout/StarryBackground";
import NotFound from "./pages/NotFound";
import AboutMe from "./pages/AboutMe";
import Docs from "./pages/Docs";

const queryClient = new QueryClient();

const ClerkTokenBridge = () => {
  const { isLoaded, isSignedIn, getToken } = useAuth();
  const lastSetRef = useRef<string>("");
  const refreshTimerRef = useRef<number | null>(null);

  const clearRefreshTimer = () => {
    if (refreshTimerRef.current != null) {
      window.clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
  };

  const getJwtExpMs = (jwt: string): number | null => {
    try {
      const parts = String(jwt || "").split(".");
      if (parts.length < 2) return null;
      const payloadB64Url = parts[1] || "";
      const payloadB64 = payloadB64Url.replace(/-/g, "+").replace(/_/g, "/");
      const padLen = (4 - (payloadB64.length % 4)) % 4;
      const padded = payloadB64 + "=".repeat(padLen);
      const json = JSON.parse(atob(padded));
      const expSeconds = typeof json?.exp === "number" ? json.exp : Number(json?.exp);
      if (!Number.isFinite(expSeconds) || expSeconds <= 0) return null;
      return expSeconds * 1000;
    } catch {
      return null;
    }
  };

  const scheduleNextRefresh = (token: string, refresh: () => void) => {
    clearRefreshTimer();

    const now = Date.now();
    const expMs = token ? getJwtExpMs(token) : null;
    const safetyMs = 90_000;
    let delayMs = 60_000;

    if (expMs && expMs > now + 5_000) {
      delayMs = Math.max(5_000, expMs - now - safetyMs);
    }

    refreshTimerRef.current = window.setTimeout(refresh, delayMs);
  };

  useEffect(() => {
    const g: any = globalThis as any;
    if (!isLoaded) return;

    if (!isSignedIn) {
      clearRefreshTimer();
      g.__calceraClerkJwt = "";
      lastSetRef.current = "";
      return;
    }

    let cancelled = false;
    const refresh = async () => {
      try {
        const t = (await getToken({ skipCache: true })) || "";
        if (cancelled) return;
        g.__calceraClerkJwt = t;
        lastSetRef.current = t;
        scheduleNextRefresh(t, () => void refresh());
      } catch {
        if (cancelled) return;
        // Keep the previous token — it may still be valid. Only schedule a retry.
        clearRefreshTimer();
        refreshTimerRef.current = window.setTimeout(() => void refresh(), 10_000);
      }
    };

    g.__calceraClerkRefreshJwt = async () => {
      await refresh();
      return String(g.__calceraClerkJwt || "");
    };

    // Recover immediately when the tab returns from background.
    // setTimeout is throttled in background tabs, so scheduled refreshes
    // may not fire in time. This ensures we get a fresh token as soon as
    // the user is actually looking at the page again.
    const onVisible = () => {
      if (document.visibilityState === "visible") {
        refresh();
      }
    };
    document.addEventListener("visibilitychange", onVisible);

    refresh();
    return () => {
      cancelled = true;
      clearRefreshTimer();
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [isLoaded, isSignedIn, getToken]);

  return null;
};

const DevSharingSmokeRunner = () => {
  const ranRef = useRef(false);
  const { isLoaded, isSignedIn, getToken } = useAuth();

  useEffect(() => {
    if (!isLoaded) return;
    if (ranRef.current) return;

    try {
      const sp = new URLSearchParams(window.location.search);
      const enabled = sp.get("sharingSmoke") === "1" || window.localStorage.getItem("calcera:sharingSmoke") === "1";
      if (!enabled) return;

      // Safety gate: only allow the smoke test on dev environments (explicit URL opt-in required above).
      // This is intentionally NOT tied to import.meta.env.DEV so it can run on hosted dev builds.
      const host = (window.location.hostname || "").toLowerCase();
      const isAllowedHost =
        host === "dev.calcera.io" ||
        host === "local.calcera.io" ||
        host === "localhost" ||
        host === "127.0.0.1" ||
        host.endsWith(".pages.dev");
      if (!isAllowedHost) {
        console.warn("[sharing-smoke:web] blocked on this hostname:", host);
        return;
      }

      ranRef.current = true;

      (async () => {
        // When enabled via URL/localStorage, prefer the *logged-in Clerk user JWT* if available.
        let ownerJwt = "";
        if (isSignedIn) {
          try {
            ownerJwt = (await getToken({ skipCache: true })) || "";
          } catch {
            ownerJwt = "";
          }
        }

        // Fallback for anon/local debugging: allow manually provided JWT.
        if (!ownerJwt.trim()) {
          ownerJwt = window.localStorage.getItem("calcera:sharingSmoke:ownerJwt") || "";
        }

        const recipientJwt = window.localStorage.getItem("calcera:sharingSmoke:recipientJwt") || "";

        if (!ownerJwt.trim()) {
          console.warn(
            "[sharing-smoke:web] enabled, but no JWT available. Sign in (preferred) or set localStorage calcera:sharingSmoke:ownerJwt, then reload.",
          );
          return;
        }

        const mod = await import("./dev/sharingSmokeWeb");
        await mod.runSharingSmokeWeb({ ownerJwt: ownerJwt.trim(), recipientJwt: recipientJwt.trim() || undefined });
      })().catch((e) => console.error("[sharing-smoke:web] FAILED", e));
    } catch (e) {
      console.error("[sharing-smoke:web] init error", e);
    }
  }, [isLoaded, isSignedIn, getToken]);

  return null;
};

const App = () => {
  return (
    <ClerkProvider
      publishableKey={import.meta.env.VITE_CLERK_PUBLISHABLE_KEY}
      signUpUrl={undefined}
      appearance={{
        elements: {
          footerAction: { display: "none" }, // removes “Don’t have an account? Sign up”
        },
      }}
    >
      <ClerkTokenBridge />
      <DevSharingSmokeRunner />
    {/* Global starry background; phrases are now managed from the workspace-scoped
        instance so we disable prompts here. */}
    <StarryBackground showParticles={false} showPrompts={false} />

    {/* lift app UI above background layers */}
    <div style={{ position: "relative", zIndex: 10 }}>
      <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<WorkspaceHome />} />
            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="/documents" element={<Docs />} />
            
            <Route path="/AboutMe" element={<AboutMe />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
    </div>
  </ClerkProvider>
);
};

createRoot(document.getElementById("root")!).render(<App />);
